import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { DateTime } from "luxon";

// mui icons
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";

// mui components
import { Box, Stack } from "@mui/material";

// custom components
import CustomTable from "../../../custom-table";
import SupportingDocs from "../../../supporting-docs";
import PrimaryButton from "../../../buttons/primary-button";
import SecondaryButton from "../../../buttons/secondary-button";
import Heading3 from "../../../typography/heading-03";
import Body2 from "../../../typography/body-02";
import Body3 from "../../../typography/body-03";

// api slice
import { useUploadAttachmentDocumentMutation } from "../../../../features/upload-attachment-document/upload-attachment-document-api-slice";
import { getMimeTypeFromFilename, GENERIC_FALLBACK_CONTENT_TYPE } from "../../../../utils/document-validation";
import { isRetryResponse, isRetryItem } from "../../../../utils/document-retry-helper";
import { useAttachDocumentConditionMutation } from "../../../../features/attach-document-condition/attach-document-condition";
import { useUnderwritingConditionMutation } from "../../../../features/underwriting-conditions/underwritingApiSlice";
import { usePreliminaryConditionMutation } from "../../../../features/preliminary-conditions/preliminaryApiSlice";
import { usePostClosingConditionMutation } from "../../../../features/post-closing-conditions/postClosingApiSlice";
import { useUpdateFieldValuesTpoMutation } from "../../../../features/update-field-value-tpo/updateFieldsApiSlice";
import { useAttachEnhancedConditionDocumentMutation } from "../../../../features/attach-enhanced-condition-document/attach-enhanced-condition-document";
import { useEnhancedConditionsDocumentsListByCategoryMutation } from "../../../../features/fetch-enhanced-conditions-documents-list-by-category/fetchEnhancedConditionsDocumentsListByCatgoryApiSlice";
import { useEnhancedConditionsTemplatesMutation } from "../../../../features/enhanced-conditions-templates/fetchEnhancedConditionsTemplatesApiSlice";
import { useEnhancedConditionsTemplateDetailsMutation } from "../../../../features/enhanced-conditions-template-details/fetchEnhancedConditionsTemplateDetailsApiSlice";
import { useEnhancedConditionsEFolderCreationMutation } from "../../../../features/create-enhanced-conditions-efolder/createEnhancedConditionsEfolderApiSlice";
import { useEnhancedConditionsDocumentsListMutation } from "../../../../features/enhanced-conditions-documents_list/fetchEnhancedConditionsDocumentsListApiSlice";
import { useEnhancedConditionsUploadAttachmentUrlAndAssignEfolderMutation } from "../../../../features/fetch-enhanced-conditions-upload-attachment-url-and-assign-efolder/fetchEnhancedConditionsUploadAttachmentUrlAndAssignEfolderApiSlice";
import { useEnhancedConditionsEfolderDocumentsListMutation } from "../../../../features/fetch-enhanced-conditions-efolder-documents-list/fetchEnhancedConditionsEfolderDocumentsApiSlice";
import { useAttachEnhancedConditionDocumentToUploadUrlMutation } from "../../../../features/attach-enhanced-condition-document-to-uploadurl/attach-enhanced-condition-document-to-uploadurl";
// redux slice
import { getUnderwritingConditions } from "../../../../app/services/underwriting-conditions";
import { getPreliminaryConditions } from "../../../../app/services/preliminary-conditions";
import { getPostClosingConditions } from "../../../../app/services/post-closing-conditions";
import { getEnhancedConditionsDocumentsListByCategory } from "../../../../app/services/enhanced-conditions-documents-lists-by-category";
import { getEnhancedConditionsDocumentsList } from "../../../../app/services/enhanced-conditions-documents-list";
// reducer slice
import { selectUnderwritingConditions } from "../../../../features/underwriting-conditions/underwritingSlice";
import { selectPreliminaryConditions } from "../../../../features/preliminary-conditions/preliminarySlice";
import { selectPostClosingConditions } from "../../../../features/post-closing-conditions/postClosingSlice";
import { selectCategoryCondition } from "../../../../features/category-condition/categoryConditonSlice";
import { selectEnhancedConditionDocumentsListByCategories } from "../../../../features/fetch-enhanced-conditions-documents-list-by-category/fetchEnhancedConditionsDocumentsListByCategory";
import { selectEnhancedConditionStatus } from "../../../../features/enhanced-condition-status/enhancedConditionStatusSlice";
import { selectUserDetails } from "../../../../features/auth/authSlice";
import { selectImpersonatedUser } from "../../../../features/admin/impersonated-user/impersonatedUserSlice";

// helper utils
import { formatBytes } from "../../../../utils/format-bytes";
import { uploadFileWithChunking } from "../../../../utils/upload-file-with-chunks";
import { uploadMultipartFile } from "../../../../utils/upload-multipart-file";

// custom styles
import styles from "./index.module.scss";

const ConditionTrackingDrawer = ({
	handleCloseDrawer,
	handleOpenProcessingModal,
	handleCloseProcessingModal,
	handleOpenSuccessModal,
	setProcessingText,
	setSuccessText,
	handleOpenLoadingDataModal,
	handleCloseLoadingDataModal,
	setLoadingDataText,
	handleOpenCommonErrorModal,
	// custom values
	selectedCondition,
	setSelectedCondition,
}) => {
	const fileTypes = [
		"PDF",
		"DOC",
		"DOCX",
		"TXT",
		"TIF",
		"JPG",
		"JPEG",
		"JPE",
		"EMF",
		"XPS",
	];
	const params = useParams();

	const [uploadAttachmentDocument] = useUploadAttachmentDocumentMutation();
	const [attachDocumentCondition] = useAttachDocumentConditionMutation();

	const [underwritingCondition] = useUnderwritingConditionMutation();
	const [preliminaryCondition] = usePreliminaryConditionMutation();
	const [postClosingCondition] = usePostClosingConditionMutation();

	const [attachEnhancedConditionDocument] =
		useAttachEnhancedConditionDocumentMutation();
	const [enhancedConditionsRequest] =
		useEnhancedConditionsDocumentsListByCategoryMutation();
	const [enhancedConditionsDocumentsList] =
		useEnhancedConditionsDocumentsListMutation();

	const [updateFieldValuesTpo] = useUpdateFieldValuesTpoMutation();

	const enhancedConditionsStatus = useSelector(selectEnhancedConditionStatus);
	const impersonatedUser = useSelector(selectImpersonatedUser);
	const [enhancedConditionsTemplates] =
		useEnhancedConditionsTemplatesMutation();
	const [enhancedConditionsTemplateDetails] =
		useEnhancedConditionsTemplateDetailsMutation();
	const [enhancedConditionsEFolderCreation] =
		useEnhancedConditionsEFolderCreationMutation();
	const [enhancedConditionsUploadUrlAndAssignEfolder] =
		useEnhancedConditionsUploadAttachmentUrlAndAssignEfolderMutation();

	const [attachEnhancedConditionDocumentToUploadUrl] =
		useAttachEnhancedConditionDocumentToUploadUrlMutation();

	const isEnhancedConditionsEnabled =
		String(enhancedConditionsStatus ?? "")
			.trim()
			.toUpperCase() === "Y";
	const currentLoggedInUserEmail = String(
		impersonatedUser?.email ?? userDetails?.email ?? ""
	).trim();

	const brokerUploadColumns = [
		{ id: "status", label: "Status", minWidth: 120 },
		{ id: "sub-status", label: "Sub Status", minWidth: 120 },
		{ id: "category", label: "Category", minWidth: 120 },
		{ id: "description", label: "Description", minWidth: 150 },
	];

	const [getEnhancedConditionsEfolderDocumentsList] =
		useEnhancedConditionsEfolderDocumentsListMutation();
	const [selectedRow, setSelectedRow] = useState([]);
	const userDetails = useSelector(selectUserDetails);

	const [documentsList, setDocumentsList] = useState([]);
	const appendChunkMetadata = (formData, meta) => {
		if (!meta?.isChunked) return;

		formData.append("chunk_index", String(meta?.chunkIndex));
		formData.append("total_chunks", String(meta?.totalChunks));
		formData.append("chunk_size", String(meta?.chunkSize));
		formData.append("upload_id", meta?.uploadId);
		formData.append("original_file_name", meta?.originalFileName);
		formData.append("original_file_size", String(meta?.originalFileSize));
		formData.append("is_chunked", "true");
		formData.append("is_first_chunk", String(meta?.isFirstChunk));
		formData.append("is_last_chunk", String(meta?.isLastChunk));
		formData.append("chunk_name", meta?.chunkName);
	};

	const enhancedConditionDocumentsListByCategories = useSelector(
		selectEnhancedConditionDocumentsListByCategories
	);
	useEffect(() => {
		const list = Array.isArray(enhancedConditionDocumentsListByCategories)
			? enhancedConditionDocumentsListByCategories
			: enhancedConditionDocumentsListByCategories?.data || [];

		setDocumentsList(list);
	}, [enhancedConditionDocumentsListByCategories]);

	useEffect(() => {
		if (selectedCondition?.length > 0) {
			setSelectedRow(selectedCondition);
		}
	}, [selectedCondition]);

	const categoryCondition = useSelector(selectCategoryCondition);

	// get underwriting conditions data from redux store
	const underwritingConditionData = useSelector(selectUnderwritingConditions);

	// get preliminary conditions data from redux store
	const preliminaryConditionData = useSelector(selectPreliminaryConditions);

	// get post closing conditions data from redux store
	const postClosingConditionData = useSelector(selectPostClosingConditions);

	// update state underwriting conditions data from redux store
	useEffect(() => {
		if (
			categoryCondition === "underwriting" &&
			underwritingConditionData !== undefined
		) {
			setDocumentsList(underwritingConditionData);
			handleCloseProcessingModal?.();
		}
	}, [
		categoryCondition,
		handleCloseProcessingModal,
		underwritingConditionData,
	]);

	// update state preliminary conditions data from redux store
	useEffect(() => {
		if (
			categoryCondition === "preliminary" &&
			preliminaryConditionData !== undefined
		) {
			setDocumentsList(preliminaryConditionData);
			handleCloseProcessingModal?.();
		}
	}, [categoryCondition, handleCloseProcessingModal, preliminaryConditionData]);

	// update state post closing conditions data from redux store
	useEffect(() => {
		if (
			categoryCondition === "postclosing" &&
			postClosingConditionData !== undefined
		) {
			setDocumentsList(postClosingConditionData);
			handleCloseProcessingModal?.();
		}
	}, [categoryCondition, handleCloseProcessingModal, postClosingConditionData]);

	const [attachedFiles, setAttachedFiles] = useState([]);

	const deleteFile = (event, index) => {
		setAttachedFiles((prev) =>
			prev.filter((_, fileIndex) => fileIndex !== index)
		);
	};

	const isConflictError = (error) =>
		error?.status === 409 ||
		error?.originalStatus === 409 ||
		error?.OriginalStatus === 409 ||
		error?.status_code === 409;

	const handleFileAttach = async (file) => {
		setAttachedFiles((prev) => [...prev, ...Array.from(file)]);
	};

	const handleCancel = () => {
		setSelectedRow([]);
		setAttachedFiles([]);
	};

	const handleErrorModal = (errorKey, data) => {
		handleCloseProcessingModal?.();

		if (isConflictError(data)) {
			handleOpenCommonErrorModal?.("CONDITION_TRACKING_LOAN_IN_USE_ERROR");
			return;
		}

		if (
			data?.status === "FETCH_ERROR" ||
			data?.status === 413 ||
			data?.OriginalStatus === 413 ||
			data?.originalStatus === 413
		) {
			// Use the new CommonErrorModal for file size limit errors
			handleOpenCommonErrorModal(
				errorKey ? errorKey : "CONDITION_FILE_SIZE_TOO_LARGE"
			);
		} else {
			// Use the new CommonErrorModal for other errors
			handleOpenCommonErrorModal(
				errorKey ? errorKey : "CONDITION_TRACKING_LOAN_IN_USE_ERROR"
			);
		}
	};

	const updateConditionData = async () => {
		if (isEnhancedConditionsEnabled) {
			try {
				const conditionsResponse =
					await getEnhancedConditionsDocumentsListByCategory({
						loanGuid: params?.id,
						conditionType: categoryCondition,
						apiRequest: enhancedConditionsRequest,
						handleErrorModal,
					});

				const loanDocsResponse = await getEnhancedConditionsDocumentsList({
					loanGuid: params?.id,
					apiRequest: enhancedConditionsDocumentsList,
					handleErrorModal,
				});

				const conditionsList = conditionsResponse?.data || [];
				const allLoanDocuments = loanDocsResponse?.data || [];

				const enrichedDocumentsList = conditionsList.map((doc) => {
					const matchedLoanDoc = allLoanDocuments.find((loanDoc) =>
						loanDoc?.conditions?.some(
							(condition) => String(condition?.entityId) === String(doc?.id)
						)
					);

					return {
						...doc,
						lastAttachmentDate: matchedLoanDoc?.lastAttachmentDate ?? null,
					};
				});

				setDocumentsList(enrichedDocumentsList);
			} catch (err) {
				handleErrorModal(err);
			}
		} else {
			if (categoryCondition === "underwriting") {
				await getUnderwritingConditions({
					loanGuid: params?.id,
					apiRequest: underwritingCondition,
					handleErrorModal: handleErrorModal,
				});

				handleOpenProcessingModal();
				setProcessingText("Setting up the lock info");
			} else if (categoryCondition === "preliminary") {
				await getPreliminaryConditions({
					loanGuid: params?.id,
					apiRequest: preliminaryCondition,
					handleErrorModal: handleErrorModal,
				});

				handleOpenProcessingModal();
				setProcessingText("Setting up the lock info");
			} else if (categoryCondition === "postclosing") {
				await getPostClosingConditions({
					loanGuid: params?.id,
					apiRequest: postClosingCondition,
					handleErrorModal: handleErrorModal,
				});

				handleOpenProcessingModal();
				setProcessingText("Setting up the lock info");
			}
		}
	};

	const mapDocumentsToConditions = (documents) => {
		const result = [];

		let attachmentIds = documents?.map((item) => item.attachment_id).join(",");

		documents.forEach((document) => {
			selectedRow.forEach((conditionId) => {
				const conditionMapping = {
					document_id: document?.document_id,
					attachment_id: document?.attachment_id,
					condition_id: conditionId,
					condition_type: categoryCondition?.toLowerCase(),
					attachment_ids: attachmentIds,
				};

				result.push(conditionMapping);
			});
		});

		return { condition_mapping: result };
	};

	const normalizeFolderName = (value) =>
		String(value ?? "")
			.trim()
			.toLowerCase();

	const getDocumentFolderName = (document) =>
		normalizeFolderName(
			document?.title ?? document?.document_name ?? document?.name
		);

	const checkIfFolderExists = async (loanGuid, selectedDocument) => {
		try {
			const loanDocuments = await getEnhancedConditionsDocumentsList({
				loanGuid,
				apiRequest: enhancedConditionsDocumentsList,
				handleErrorModal,
				errorKey: "ENHANCED_CONDITION_LOAN_DOCUMENTS_UNAVAILABLE",
			});

			if (!loanDocuments) {
				return null;
			}

			const selectedFolderName = normalizeFolderName(selectedDocument?.title);
			const matchedFolder = loanDocuments?.data?.find((doc) => {
				const existingFolderName = getDocumentFolderName(doc);

				return (
					existingFolderName === selectedFolderName ||
					existingFolderName.includes(selectedFolderName) ||
					selectedFolderName.includes(existingFolderName)
				);
			});

			if (matchedFolder) {
				return matchedFolder?.id;
			}

			const createdFolder = await enhancedConditionsEFolderCreation({
				documentName: selectedDocument?.title,
				description:
					selectedDocument?.description ||
					selectedDocument?.internalDescription ||
					selectedDocument?.externalDescription,
				loanGuid: params?.id,
			}).unwrap();

			return createdFolder?.data?.[0]?.id ?? null;
		} catch (error) {
			handleErrorModal("ENHANCED_CONDITION_EFOLDER_CREATION_FAILED", error);
			return null;
		}
	};
	const efolderDetails = async (selectedDocument) => {
		try {
			const templatesResponse = await enhancedConditionsTemplates().unwrap();

			const matchedTemplate = templatesResponse?.data?.find((item) =>
				selectedDocument?.title?.includes(item.title)
			);

			const selectedId = matchedTemplate?.id;

			if (!selectedId) {
				handleErrorModal("ENHANCED_CONDITION_UPLOAD_FAILED", templatesResponse);
				return null;
			}

			const templateDetails = await enhancedConditionsTemplateDetails({
				id: selectedId,
			}).unwrap();

			const folderNameCondition =
				templateDetails?.data?.connectSettings?.documentOption;

			if (folderNameCondition === "MatchConditionName") {
				return checkIfFolderExists(params?.id, selectedDocument);
			}

			const efolderDocuments =
				await getEnhancedConditionsEfolderDocumentsList().unwrap();

			const documentName = efolderDocuments?.data?.find(
				(doc) =>
					doc?.id ===
					templateDetails?.data?.connectSettings?.documentTemplate?.entityId
			)?.name;

			return checkIfFolderExists(params?.id, {
				title: documentName,
				internalDescription: `Default Document with name ${documentName}`,
			});
		} catch (error) {
			handleErrorModal("ENHANCED_CONDITION_UPLOAD_FAILED", error);
			return null;
		}
	};

	const buildEnhancedUploadBody = (file, efolderId) => ({
		user_email: currentLoggedInUserEmail,
		file: {
			contentType:
				file?.type ||
				getMimeTypeFromFilename(file?.name) ||
				GENERIC_FALLBACK_CONTENT_TYPE,
			name: file?.name,
			size: file?.size ?? 0,
		},
		title: file?.name,
		assignTo: {
			entityId: efolderId,
			entityType: "Document",
		},
	});

	const getEnhancedUploadRequest = async (enhancedUploadBody) => {
		try {
			const uploadDocumentResponse =
				await enhancedConditionsUploadUrlAndAssignEfolder({
					loanGuid: params?.id,
					body: enhancedUploadBody,
				}).unwrap();

			const uploadData = uploadDocumentResponse?.data ?? {};
			const uploadUrl = uploadData?.multiChunkRequired
				? uploadData?.multiChunk?.commitUrl
				: uploadData?.uploadUrl || uploadData?.url;
			const uploadAuthorization =
				uploadData?.authorizationHeader ||
				uploadData?.authorization ||
				uploadData?.authHeader ||
				uploadData?.auth ||
				uploadData?.signature;

			if (!uploadUrl) {
				handleErrorModal(
					"ENHANCED_CONDITION_UPLOAD_FAILED",
					uploadDocumentResponse
				);
				return null;
			}

			return {
				uploadUrl,
				uploadAuthorization,
				multiChunkRequired: uploadData?.multiChunkRequired,
				multiChunk: uploadData?.multiChunk,
			};
		} catch (error) {
			handleErrorModal("ENHANCED_CONDITION_UPLOAD_FAILED", error);
			return null;
		}
	};

	const uploadEnhancedConditionFile = async ({
		uploadUrl,
		uploadAuthorization,
		multiChunkRequired,
		multiChunk,
		file,
		conditionId,
		conditionName,
		entityId,
		entityType,
		title,
	}) => {
		try {
			if (multiChunkRequired || Array.isArray(multiChunk?.chunkList)) {
				return await uploadMultipartFile({
					file,
					chunkList: multiChunk?.chunkList,
					commitUrl: uploadUrl,
					authorization: uploadAuthorization,
				});
			}

			const uploadResponse = await attachEnhancedConditionDocumentToUploadUrl({
				uploadUrl,
				authorization: uploadAuthorization,
				file,
				user_email: currentLoggedInUserEmail,
				loanGuid: params?.id,
				conditionId,
				conditionName,
				entityId,
				entityType,
				title,
			}).unwrap();

			if (isRetryResponse(uploadResponse)) {
				handleCloseProcessingModal?.();
				handleOpenCommonErrorModal?.("DOCUMENT_RETRY_PENDING");
				return null;
			}

			return uploadResponse;
		} catch (error) {
			handleErrorModal("ENHANCED_CONDITION_DOCUMENT_UPLOAD_FAILED", error);
			return null;
		}
	};

	const uploadConditionAttachmentFile = async (element) =>
		uploadFileWithChunking({
			file: element,
			uploadPart: async ({ file: uploadFile, meta }) => {
				const formData = new FormData();
				formData.append("loan_guid", params?.id);
				formData.append(
					"file",
					uploadFile,
					meta?.chunkName || uploadFile?.name || element?.name
				);
				appendChunkMetadata(formData, meta);

				return uploadAttachmentDocument({
					formData,
				}).unwrap();
			},
		});

	const attachEnhancedConditionDocumentRecord = async ({
		efolderId,
		selectedDocument,
	}) => {
		try {
			const attachDocumentResponse = await attachEnhancedConditionDocument({
				loanGuid: params?.id,
				data: [
					{
						entityId: efolderId,
						entityType: "Document",
					},
				],
				conditionId: selectedDocument?.id,
			}).unwrap();

			const responseItems = attachDocumentResponse?.data ?? [];

			if (responseItems.some(isRetryItem)) {
				handleCloseProcessingModal?.();
				handleOpenCommonErrorModal?.("DOCUMENT_RETRY_PENDING");
				return false;
			}

			if (
				responseItems.length === 0 ||
				responseItems.some((item) => item?.status === "failure")
			) {
				handleErrorModal(
					"ENHANCED_CONDITION_DOCUMENT_ASSIGNMENT_FAILED",
					attachDocumentResponse
				);
				return false;
			}

			return true;
		} catch (error) {
			handleErrorModal("ENHANCED_CONDITION_DOCUMENT_ASSIGNMENT_FAILED", error);
			return false;
		}
	};

	const getSelectedEnhancedDocuments = () =>
		(selectedRow || [])
			.map((selectedId) =>
				documentsList?.find((doc) => String(doc?.id) === String(selectedId))
			)
			.filter(Boolean);

	// upload documents
	const handleSubmitDocuments = async () => {
		if (selectedRow?.length === 0) return;
		if (attachedFiles?.length === 0) return;

		if (isEnhancedConditionsEnabled) {
			const selectedDocuments = getSelectedEnhancedDocuments();
			console.log("submit documents payload:", {
				isEnhancedConditionsEnabled,
				selectedRow,
				selectedDocuments,
				attachedFiles,
			});

			handleCloseDrawer?.();
			handleOpenProcessingModal?.();
			setProcessingText?.("Your Remedy is being updated");

			try {
				if (selectedDocuments.length === 0) {
					throw new Error("Selected document not found");
				}

				for (const selectedDocument of selectedDocuments) {
					const efolderId = await efolderDetails(selectedDocument);

					if (!efolderId) {
						return;
					}

					for (const file of attachedFiles) {
						const uploadDetails = await getEnhancedUploadRequest(
							buildEnhancedUploadBody(file, efolderId)
						);

						if (!uploadDetails) {
							return;
						}

						const uploadResponse = await uploadEnhancedConditionFile({
							uploadUrl: uploadDetails.uploadUrl,
							uploadAuthorization: uploadDetails.uploadAuthorization,
							multiChunkRequired: uploadDetails.multiChunkRequired,
							multiChunk: uploadDetails.multiChunk,
							file,
							conditionId: selectedDocument?.id,
							conditionName: selectedDocument?.label ?? selectedDocument?.title,
							entityId: efolderId,
							entityType: "Document",
							title: file?.name,
						});

						if (!uploadResponse) {
							return;
						}
					}

					const attached = await attachEnhancedConditionDocumentRecord({
						efolderId,
						selectedDocument,
					});

					if (!attached) {
						return;
					}
				}

				handleCloseProcessingModal?.();
				handleOpenLoadingDataModal?.();
				setLoadingDataText?.("Setting up the conditions list");
				await updateConditionData();
				handleCloseLoadingDataModal?.();
				setSelectedCondition("");
				handleOpenSuccessModal?.();
				setSuccessText?.("Remedy has been added");
			} catch (err) {
				handleCloseLoadingDataModal?.();
				handleErrorModal("ENHANCED_CONDITION_UPLOAD_FAILED", err);
				setSelectedCondition("");
			} finally {
				handleCloseProcessingModal?.();
			}
			return;
		}

		let valuesArr = [
			{ id: "TPO.X4", value: DateTime.now().toFormat("MM-dd-yyyy") },
			{
				id: "CX.LC.DOC.REC.DATE",
				value: DateTime.fromISO(DateTime.now(), {
					zone: "America/New_York",
				}).toFormat("MM-dd-yyyy hh:mm a"),
			},
		];

		console.log("submit documents payload:", {
			isEnhancedConditionsEnabled,
			selectedRow,
			attachedFiles,
			valuesArr,
		});

		handleCloseDrawer?.();
		handleOpenProcessingModal?.();
		setProcessingText?.("Your Remedy is being updated");

		try {
			await updateFieldValuesTpo({
				loan_guid: params?.id,
				field_values: valuesArr,
			}).unwrap();

			let lastUploadResponse = null;

			for (const file of attachedFiles) {
				lastUploadResponse = await uploadConditionAttachmentFile(file);

				if (!lastUploadResponse) {
					return;
				}
			}

			const mappedDocuments = mapDocumentsToConditions(
				lastUploadResponse?.data ?? []
			);

			const attachDocumentResponse = await attachDocumentCondition({
				loanGuid: params?.id,
				data: mappedDocuments,
			}).unwrap();

			if (
				attachDocumentResponse?.data?.length > 0 &&
				attachDocumentResponse?.data[0]?.status === "success"
			) {
				handleCloseProcessingModal?.();
				handleOpenLoadingDataModal?.();
				setLoadingDataText?.("Setting up the conditions list");
				await updateConditionData();
				handleCloseLoadingDataModal?.();
				setSelectedCondition("");
				handleOpenSuccessModal?.();
				setSuccessText("Remedy has been added");
				return;
			}

			throw new Error("CONDITION_TRACKING_LOAN_IN_USE_ERROR");
		} catch (err) {
			handleCloseLoadingDataModal?.();
			handleErrorModal("CONDITION_TRACKING_LOAN_IN_USE_ERROR", err);
			setSelectedCondition("");
		} finally {
			handleCloseProcessingModal?.();
		}
	};

	return (
		<Stack direction="column" className={styles.stackContainer}>
			<Stack
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				className={styles.drawerHeaderContainer}
			>
				<Heading3
					text="Add Remedy"
					extraClass={styles.headerText}
					fontType="semibold"
				/>
				<SecondaryButton
					startIcon={<CloseIcon />}
					text="Close"
					onClick={() => {
						setSelectedCondition("");
						handleCloseDrawer();
					}}
				/>
			</Stack>

			<Stack direction="column" className={styles.conditionsContainer}>
				<Heading3
					text="Choose all conditions being satisfied"
					extraClass={styles.labelText}
					fontType="semibold"
				/>

				<CustomTable
					tableType="satisfying-conditions"
					squaredBorder={true}
					maxHeight={true}
					columnData={brokerUploadColumns}
					rowData={documentsList}
					setSortedData={setDocumentsList}
					selectedRow={selectedRow}
					setSelectedRow={setSelectedRow}
				/>
			</Stack>

			<Stack direction="column" className={styles.docsContainer}>
				<Heading3
					text="Upload Supporting Documentation:"
					extraClass={styles.labelText}
					fontType="semibold"
				/>

				<SupportingDocs
					fileCount={attachedFiles?.length}
					fileTypes={fileTypes}
					text="or drag to upload supporting files"
					handleFileAttach={handleFileAttach}
					multiple={true}
					onUnsupportedFileType={() =>
						handleOpenCommonErrorModal("CONDITION_FILE_TYPE_NOT_SUPPORTED")
					}
				/>
			</Stack>

			{attachedFiles !== undefined && attachedFiles?.length > 0 && (
				<Stack direction="column" className={styles.uploadedFileContainer}>
					{attachedFiles?.map((file, index) => (
						<Stack
							direction="row"
							key={index}
							className={styles.fileListContainer}
						>
							<Box className={styles.fileIconContainer}>
								<UploadFileIcon className={styles.fileIcon} />
							</Box>
							<Stack direction="column" className={styles.fileNameContainer}>
								<Body2 text={file?.name} />
								<Body3 text={formatBytes(file?.size)} />
							</Stack>

							<Box
								sx={{ cursor: "pointer" }}
								onClick={(event) => deleteFile(event, index)}
								className={styles.deleteIconContainer}
							>
								<CloseIcon className={styles.deleteIcon} />
							</Box>
						</Stack>
					))}
				</Stack>
			)}

			<Stack
				direction="row"
				alignItems="center"
				justifyContent="flex-end"
				className={styles.drawerFooterContainer}
			>
				<SecondaryButton
					text="Reset"
					extraClass={styles.cancelBtn}
					extraTextClass={styles.cancelText}
					onClick={handleCancel}
					disabled={selectedRow?.length === 0 && attachedFiles?.length === 0}
				/>
				<PrimaryButton
					text="Upload"
					onClick={handleSubmitDocuments}
					disabled={selectedRow?.length === 0 || attachedFiles?.length === 0}
				/>
			</Stack>
		</Stack>
	);
};

export default ConditionTrackingDrawer;
